import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, defer, from, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { UserService } from './user.service';
import { ReplayProtectionService } from './replay-protection.service';

export interface NavigationTab_Dto {
    moduleName: string;
    tabName: string;
    componentRoute: string;
    sortOrder: number;
}

export interface NavigationConfig_Dto {
    // Role + customer the slice was fetched for. Used to invalidate the cache when the
    // session identity changes (Switch Customer / backupSession, or a re-login in the
    // same tab) so a stale role never keeps its old permissions.
    role: string;
    customerId: number;
    routes: string[];
    tabs: NavigationTab_Dto[];
}

/**
 * Role-scoped navigation config (allowed routes + module tabs), served by
 * GET /api/MenuHandler/GetNavigationConfig.
 *
 * Replaces the static src/data/ValidRouts.ts and src/data/tabData.ts, which shipped
 * EVERY role's permission matrix inside the JS bundle — any user could read the full
 * cross-role map in DevTools and hand-craft a navigation to another role's screen
 * (Broken Access Control). The API derives role and customerId from the caller's JWT
 * claims, so a client can only ever obtain its own slice.
 *
 * IN-MEMORY CACHE ONLY: nothing is written to sessionStorage/localStorage, so the
 * config is not visible under DevTools → Application. The cache therefore dies on a
 * hard refresh or in a new tab and the API is re-called once per page load — the same
 * model the sidebar menu already uses. The in-flight shareReplay guarantees the guard,
 * the tab bar and any parallel navigations still trigger at most ONE request per load.
 */
@Injectable({
    providedIn: 'root'
})
export class NavigationConfigService {

    private apiUrl = environment.apiUrl;
    private config: NavigationConfig_Dto | null = null;
    private inFlight$: Observable<NavigationConfig_Dto> | null = null;

    constructor(private http: HttpClient, private userService: UserService, private replayProtection: ReplayProtectionService) { }

    getConfig(): Observable<NavigationConfig_Dto> {
        const role = this.userService.getRole();
        const customerId = this.userService.getCustomerId();

        if (this.config && this.config.role === role && this.config.customerId === customerId) {
            return of(this.config);
        }
        this.config = null;

        // GetNavigationConfig is [Authorize]. Firing it before the access token has been
        // stored — the window between a login response arriving and the session being
        // written, or any caller running on an unauthenticated screen — would send an
        // empty Bearer and get a 401 back. Fail fast instead of making that request; the
        // guard treats the failure as "not authorised yet" and routes to /Login.
        const accessToken = this.userService.getAccessToken();
        if (!accessToken) {
            return throwError(() => new Error('No access token available for navigation config'));
        }

        if (!this.inFlight$) {
            const url = `${this.apiUrl}/api/MenuHandler/GetNavigationConfig`;
            // ReplayProtectionMiddleware rejects every GET without X-Request-Timestamp/X-Nonce
            // (400 "X-Request-Timestamp header is required.") - same read headers as every other GET.
            // defer(): the nonce must be generated per SUBSCRIPTION, not once when this pipeline is
            // built. shareReplay re-subscribes to its source after an error, and a promise created
            // eagerly would replay the SAME headers - the server then answers 409 "Nonce already
            // used. Possible replay attack."
            this.inFlight$ = defer(() => from(
                this.replayProtection.getReadHeaders('GetNavigationConfig', String(this.userService.getUserId()))
            )).pipe(
                switchMap(replayHeaders => this.http.get<any>(url, {
                    headers: new HttpHeaders({
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                        ...replayHeaders
                    })
                })),
                map(body => ({
                    role,
                    customerId,
                    routes: body?.routes ?? [],
                    tabs: body?.tabs ?? []
                }) as NavigationConfig_Dto),
                tap(cfg => {
                    this.config = cfg;
                    this.inFlight$ = null;
                }),
                shareReplay(1),
                catchError(err => {
                    this.inFlight$ = null;
                    return throwError(() => err);
                })
            );
        }
        return this.inFlight$;
    }

    /**
     * Cache-only read for synchronous consumers (tab bar, analytics).
     * Null until the first successful fetch.
     */
    getConfigSync(): NavigationConfig_Dto | null {
        const role = this.userService.getRole();
        const customerId = this.userService.getCustomerId();
        return this.config && this.config.role === role && this.config.customerId === customerId
            ? this.config
            : null;
    }

    clearCache(): void {
        this.config = null;
        this.inFlight$ = null;
    }
}
